import React, { useState } from 'react';

interface AddKidModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; avatar_emoji: string; color: string }) => Promise<void>;
}

const KID_AVATARS = ['🦁', '🦄', '🐲', '🐻', '🐼', '🦊', '🐶', '🐱', '🚀', '🦖', '🦸', '🌟', '🎨', '⚽', '👑', '🤖'];
const COLOR_OPTIONS = ['#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#3b82f6', '#06b6d4', '#f43f5e', '#6366f1'];

export const AddKidModal: React.FC<AddKidModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  if (!isOpen) return null;

  const [name, setName] = useState('');
  const [avatarEmoji, setAvatarEmoji] = useState('🦁');
  const [color, setColor] = useState('#8b5cf6');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const MAX_NAME_LENGTH = 12;
  const isTooLong = name.trim().length > MAX_NAME_LENGTH;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a name');
      return;
    }

    if (trimmed.length > MAX_NAME_LENGTH) {
      setError(`Kid name cannot exceed ${MAX_NAME_LENGTH} characters. Please use a nickname instead!`);
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await onSubmit({
        name: trimmed,
        avatar_emoji: avatarEmoji,
        color,
      });
      setName('');
      setAvatarEmoji('🦁');
      setColor('#8b5cf6');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create kid profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header">
          <h3 className="modal-title">Add Kid Profile</h3>
          <button
            id="btn-close-kid-modal"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 14px',
              background: '#fee2e2',
              color: '#dc2626',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 700,
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: '8px',
              }}
            >
              <label
                className="form-label"
                htmlFor="kid-name-input"
                style={{ marginBottom: 0 }}
              >
                Kid's Name
              </label>
              <span
                id="kid-name-counter"
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: isTooLong ? '#ef4444' : name.length === MAX_NAME_LENGTH ? '#f59e0b' : '#94a3b8',
                  transition: 'color 0.2s ease',
                }}
              >
                {name.length}/{MAX_NAME_LENGTH}
              </span>
            </div>
            <input
              id="kid-name-input"
              className="form-input"
              type="text"
              placeholder="e.g. Leo or Mia"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError('');
              }}
              style={
                isTooLong
                  ? {
                      borderColor: '#f59e0b',
                      backgroundColor: '#fffbeb',
                      boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.18)',
                    }
                  : undefined
              }
              aria-invalid={isTooLong}
              aria-describedby={isTooLong ? 'kid-name-warning' : 'kid-name-counter'}
              autoFocus
              required
            />
            {isTooLong && (
              <div
                id="kid-name-warning"
                role="alert"
                style={{
                  marginTop: '10px',
                  padding: '10px 14px',
                  background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                  border: '1.5px solid #fcd34d',
                  borderRadius: '12px',
                  color: '#92400e',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: 1 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 800, color: '#78350f', marginBottom: '2px' }}>
                    Maximum 12 characters ({name.length}/{MAX_NAME_LENGTH})
                  </div>
                  <div>Please use a nickname instead!</div>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Choose Avatar Emoji</label>
            <div className="icon-grid">
              {KID_AVATARS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={`icon-option-btn ${avatarEmoji === emoji ? 'selected' : ''}`}
                  onClick={() => setAvatarEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Profile Accent Color</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: c,
                    border: color === c ? '3px solid #1e1b4b' : '2px solid transparent',
                    cursor: 'pointer',
                    boxShadow: color === c ? '0 0 0 2px #fff inset' : 'none',
                  }}
                  onClick={() => setColor(c)}
                  aria-label={`Select color ${c}`}
                />
              ))}
            </div>
          </div>

          <button
            id="btn-submit-add-kid"
            type="submit"
            className="btn-primary-gradient"
            disabled={submitting || isTooLong}
            style={
              isTooLong
                ? {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    filter: 'grayscale(0.35)',
                  }
                : undefined
            }
          >
            {submitting
              ? 'Creating...'
              : isTooLong
              ? 'Name too long (use a nickname)'
              : 'Create Kid Profile ✨'}
          </button>
        </form>
      </div>
    </div>
  );
};
